import { useState, useEffect, createContext, useContext, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { User } from "@supabase/supabase-js";

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue>({
  user: null,
  loading: true,
  signOut: async () => {},
});

/**
 * AuthProvider — single auth subscription shared across all consumers.
 * Eliminates duplicate is_disabled queries (was 8+ per page load).
 */
export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Track which user IDs we already checked to avoid duplicate calls
    const checkedUsers = new Set<string>();

    const checkDisabled = async (authUser: User): Promise<boolean> => {
      if (checkedUsers.has(authUser.id)) return false;
      checkedUsers.add(authUser.id);
      try {
        const { data } = await supabase
          .from('profiles')
          .select('is_disabled')
          .eq('user_id', authUser.id)
          .maybeSingle();

        if (data?.is_disabled) {
          await supabase.auth.signOut();
          setUser(null);
          return true;
        }
      } catch {
        // If check fails, allow login (fail open for profile read errors)
      }
      return false;
    };

    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        const authUser = session?.user ?? null;

        if (authUser && (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED')) {
          setUser(authUser);
          setLoading(false);
          // Deferred disabled check — only once per user.id
          setTimeout(() => { checkDisabled(authUser); }, 0);
        } else {
          setUser(authUser);
          setLoading(false);
        }
      }
    );

    // Then check for existing session
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      const authUser = session?.user ?? null;
      if (authUser) {
        const disabled = await checkDisabled(authUser);
        if (disabled) {
          setLoading(false);
          return;
        }
      }
      setUser(authUser);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider value={{ user, loading, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

/**
 * useAuth — now reads from shared context instead of creating
 * a separate subscription per component.
 */
export const useAuth = () => useContext(AuthContext);
